// frontend/src/pages/crm/FieldAgentDetailPage.tsx
// Per-rep drill-down — Phase 5 (2026-07-29), spec §7 "one at a time".
// First client for GET /field-agents/:userId, which shipped in Phase 4 as an
// orphan endpoint with no screen.
//
// 🔒 ONE date control, exactly as FieldAgentsPage and FieldSessionsPage: a
// one-day range IS a single day. Two <input type="date"> feed the SAME request.
//
// 🔒 Everything visual comes from fieldFormat.tsx. The board and this page must
// never be able to disagree about what orange means or how a target with no
// value is worded.
//
// 🔒 The conversion matrix is RENDERED from the server's `agent.matrix`, never
// recomputed from the leads array — a client-side recount would drift.
//
// ⚠️ KNOWN GAP, NOT FIXED HERE: `GET /field-agents/:userId` selects
// contactId / dealId / leadImportId on meetings but does NOT include the
// related rows, unlike `GET /field-sessions` (MEETING_SELECT). meetingSubject()
// therefore falls through to the address on this page where it names the deal
// or contact on the rep's own page. Widening the API was explicitly out of
// scope for this build; it is in the report.
//
// ⚠️ NEVER RENDERED. The frontend does not compile locally (~71k pre-existing
// errors; react does not resolve), so this page is UNVERIFIED.
//
// Spec: docs/superpowers/specs/2026-07-29-field-activity-and-sessions-design.md

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  AxisChip, Badge, DOT, Empty, LadderTiles, Section, TD, TH, Tile,
  duration, fullName, hhmm, isLoggedMeeting, km, meetingSubject, rupees, ymd,
  type Attainment,
} from './fieldFormat';

type Agent = {
  user: { id: string; firstName: string; lastName: string; email: string; designation: string | null };
  sessionCount: number;
  openSession: { id: string } | null;
  leadsCaptured: number;
  meetingsHeld: number;
  coloursAdvanced: number;
  daysOut: number;
  distanceMetres: number;
  claimedDistanceMetres: number;
  matrix: Record<string, number>;
  taDa: { draft: number; submitted: number; approved: number; rejected: number; none: number };
  attainment: Attainment;
  hasTarget: boolean;
};

export function FieldAgentDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const today = ymd(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [meetings, setMeetings] = useState<any[]>([]);
  const [isSingleDay, setIsSingleDay] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/field-agents/${userId}`, { params: { from, to } });
      const data = res.data?.data ?? {};
      setAgent(data.agent ?? null);
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setMeetings(Array.isArray(data.meetings) ? data.meetings : []);
      setIsSingleDay(data.range?.isSingleDay !== false);
    } catch (e: any) {
      // 404 here means "no field activity for this person in this window", not
      // "no such person" — say that rather than showing a bare error code.
      const status = e?.response?.status;
      setError(
        status === 404
          ? 'No field activity for this person in this period.'
          : e?.response?.data?.error || e?.message || 'Could not load this field agent',
      );
      setAgent(null);
      setSessions([]);
      setLeads([]);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [userId, from, to]);

  useEffect(() => { load(); }, [load]);

  const heading = isSingleDay ? from : `${from} → ${to}`;

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {agent ? fullName(agent.user) : 'Field agent'}
        </h1>
        <Link to="/field-agents" style={{ fontSize: 14, color: '#6B7FD6' }}>← All field agents</Link>
      </div>
      {agent?.user.designation && (
        <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>{agent.user.designation}</p>
      )}

      {/* ONE date control. A one-day range IS a single day. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0 8px' }}>
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <span style={{ color: '#9ca3af' }}>→</span>
        <input
          type="date"
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
        />
        <button
          onClick={() => { setFrom(today); setTo(today); }}
          style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Today
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>{heading}</span>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {loading && <p style={{ color: '#6b7280' }}>Loading…</p>}

      {!loading && agent && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            <Tile label="Leads captured" value={agent.leadsCaptured} color="#6b7280" />
            <Tile label="Meetings held" value={agent.meetingsHeld} color="#6b7280" />
            <Tile label="Colours advanced" value={agent.coloursAdvanced} color="#6b7280" />
            <Tile label="Days out" value={agent.daysOut} color="#6b7280" />
          </div>

          {/* Conversion matrix — rendered from the server's counts, never
              recomputed from `leads`, via the shared ladder renderer. */}
          <LadderTiles matrix={agent.matrix} />

          <p style={{ fontSize: 13, color: '#6b7280', margin: '10px 0 0' }}>
            {agent.sessionCount} session{agent.sessionCount === 1 ? '' : 's'} ·
            {' '}measured {km(agent.distanceMetres)} · claimed {km(agent.claimedDistanceMetres)}
            {agent.openSession ? ' · out now' : ''}
          </p>

          <Section title="Targets">
            {!agent.hasTarget ? (
              <Empty>No target set for this period.</Empty>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                <AxisChip label="Leads" a={agent.attainment.leadsCaptured} />
                <AxisChip label="Meetings" a={agent.attainment.meetingsHeld} />
                <AxisChip label="Colours advanced" a={agent.attainment.coloursAdvanced} />
                <AxisChip label="Days out" a={agent.attainment.daysOut} />
              </div>
            )}
          </Section>

          <Section title="Sessions">
            {sessions.length === 0 ? (
              <Empty>No field session recorded for this period.</Empty>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                    <th style={TH}>Start</th>
                    <th style={TH}>End</th>
                    <th style={TH}>Duration</th>
                    <th style={TH}>Measured</th>
                    <th style={TH}>Claimed</th>
                    <th style={TH}>Entitlement</th>
                    <th style={TH}>Claim</th>
                    <th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s: any) => (
                    <tr key={s.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={TD}>{hhmm(s.startedAt)}</td>
                      <td style={TD}>{hhmm(s.endedAt)}</td>
                      <td style={TD}>{duration(s.startedAt, s.endedAt)}</td>
                      <td style={TD}>{km(s.measuredDistanceMetres)}</td>
                      <td style={TD}>{km(s.claimedDistanceMetres)}</td>
                      {/* 🔒 ₹0.00 is a real, normal value — the rate card ships
                          empty — and is shown next to the claim so the gap is
                          visible rather than implied. */}
                      <td style={TD}>{rupees(s.entitlementPaise)}</td>
                      <td style={{ ...TD, fontWeight: 700 }}>{rupees(s.claimedAmountPaise)}</td>
                      <td style={TD}>
                        {s.taDaStatus && <Badge>{s.taDaStatus}</Badge>}
                        {s.endSource === 'auto_end_of_day' && <Badge>auto-stopped</Badge>}
                        {s.startSource === 'auto_field_lead' && <Badge>auto-started</Badge>}
                        {!s.endedAt && <Badge>running</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Field leads">
            {leads.length === 0 ? (
              <Empty>No sites captured in this period.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {leads.map((l: any) => (
                  <li
                    key={l.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #f3f4f6' }}
                  >
                    <span
                      style={{
                        width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                        background: DOT[l.fieldActivityStatus ?? ''] ?? '#9ca3af',
                      }}
                    />
                    <Link to={`/leads/${l.id}`} style={{ fontWeight: 600, color: '#111827', textDecoration: 'none' }}>
                      {l.fieldLeadTitle?.trim() || 'Unnamed site'}
                    </Link>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>{l.fieldActivityStatus ?? 'unknown'}</span>
                    {l.leadLat != null && l.leadLng != null && (
                      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 12 }}>
                        {l.leadLat.toFixed(4)}, {l.leadLng.toFixed(4)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Meetings">
            {meetings.length === 0 ? (
              <Empty>Meetings logged in the field will appear here.</Empty>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {meetings.map((m: any, i: number) => (
                  <li
                    key={m?.id ?? i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 0', borderTop: '1px solid #f3f4f6',
                    }}
                  >
                    <span style={{ color: '#6b7280', fontSize: 13, minWidth: 52 }}>
                      {hhmm(m?.scheduledAt)}
                    </span>
                    <span style={{ fontWeight: 600, color: '#111827' }}>{meetingSubject(m)}</span>
                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                      {[
                        typeof m?.durationMin === 'number' ? `${m.durationMin} min` : null,
                        m?.mode,
                        (m?.addressLine1 ?? '').trim() || null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      {/* 🔒 logged-after-the-fact vs planned — never collapsed. */}
                      <Badge>{isLoggedMeeting(m) ? 'logged' : 'scheduled'}</Badge>
                      {m?.status && m.status !== 'scheduled' && <Badge>{m.status}</Badge>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Travel claims">
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
              {agent.taDa.draft} draft · {agent.taDa.submitted} awaiting a decision ·
              {' '}{agent.taDa.approved} approved · {agent.taDa.rejected} rejected
            </p>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              Approve or reject from the field agents board, where every claim awaiting you is
              listed regardless of the dates shown here.
            </p>
          </Section>
        </>
      )}
    </div>
  );
}
